import React, { useEffect, useState, useMemo } from "react";
import { Button, Typography, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Container, MenuItem, CircularProgress, Grid, Box, Autocomplete, Chip, Alert, Collapse, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import debounce from 'lodash/debounce';
import { useNavigate, Link } from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import Users from "../../collections/users";
import Products from "../../collections/products";
import Product from "../../models/product";
import Order from "../../models/order";
import UserPackage from "../../models/user_package";
import TagInput from "../common/TagInput";

const AddOrder = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState(new Users());
  const [products, setProducts] = useState([]);
  const [currentOrderType, setCurrentOrderType] = useState('MONTHLY_PACKAGE_ORDER');
  const [orderItems, setOrderItems] = useState([{ id: 1, product: '', quantity: 1, order_type: 'MONTHLY_PACKAGE_ORDER' }]);
  const [loading, setLoading] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [address, setAddress] = useState([]);
  const [userId, setUserId] = useState([]);
  const [errors, setErrors] = useState({
    shippingCost: '',
    address: '',
    orderType: '',
    activePackage: ''
  });
  const [activePackage, setActivePackage] = useState(null);
  const [shippingCost, setShippingCost] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [orderTags, setOrderTags] = useState('');
  const [selectedUserTags, setSelectedUserTags] = useState([]);
  const [comboInfo, setComboInfo] = useState({}); // Store combo components and availability for each item
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [inventoryWarnings, setInventoryWarnings] = useState([]);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [duplicateOrderDialogOpen, setDuplicateOrderDialogOpen] = useState(false);
  const [existingOrders, setExistingOrders] = useState([]);

  const searchCustomers = async (searchQuery) => {
    try {
      setCustomerLoading(true);
      const usersInstance = new Users();
      await usersInstance.fetch(1, 100, {}, searchQuery);
      setCustomerOptions(usersInstance.items);
    } catch (error) {
      console.error('Error searching customers:', error);
      toast.error('Lỗi tìm kiếm khách hàng');
    } finally {
      setCustomerLoading(false);
    }
  };

  const debouncedSearchCustomers = useMemo(
    () => debounce(searchCustomers, 300),
    []
  );

  useEffect(() => {
    if (mounted) {
      return;
    }
    setMounted(true);

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const listProducts = new Products();
        await listProducts.fetch('ACTIVE'); // Only fetch active products for order creation
        setProducts(listProducts.items);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Lỗi tải danh sách sản phẩm');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearchCustomers.cancel();
    };
  }, [debouncedSearchCustomers]);

  const handleAddItem = () => {
    setOrderItems(prevItems => [
      ...prevItems,
      { id: prevItems.length + 1, product: '', quantity: 1, order_type: currentOrderType }
    ]);
  };

  const handleProductChange = async (index, value) => {
    // Check for duplicate product
    if (value) {
      const isDuplicate = orderItems.some((item, i) => i !== index && item.product === value);
      if (isDuplicate) {
        const duplicateProduct = products.find(p => p.id === value);
        toast.error(`Sản phẩm "${duplicateProduct?.name || value}" đã có trong đơn hàng. Vui lòng cập nhật số lượng thay vì thêm mới.`, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return; // Don't update the state
      }
    }
    const newItems = [...orderItems];
    newItems[index].product = value;
    setOrderItems(newItems);

    // Check if selected product is a combo
    const selectedProduct = products.find(p => p.id === value);
    if (selectedProduct && selectedProduct.is_combo) {
      try {
        const productInstance = new Product();
        const components = await productInstance.getComboComponents(value);

        // Store combo info for this order item
        setComboInfo(prev => ({
          ...prev,
          [index]: {
            components: components,
            isCombo: true,
          }
        }));
      } catch (error) {
        console.error('Error fetching combo components:', error);
        toast.error('Lỗi khi tải thông tin combo');
      }
    } else {
      // Clear combo info if not a combo
      setComboInfo(prev => {
        const newInfo = { ...prev };
        delete newInfo[index];
        return newInfo;
      });
    }
  };

  const handleCustomerChange = async (newValue) => {
    if (newValue) {
      setAddress(newValue.address);
      setUserId(newValue.id);

      // Capture user tags for inheritance
      if (newValue.tags) {
        const userTagsArray = newValue.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        setSelectedUserTags(userTagsArray);
      } else {
        setSelectedUserTags([]);
      }

      // Check for active package when user is selected
      try {
        const userPackageInstance = new UserPackage();
        const activePackageData = await userPackageInstance.getActivePackageByUserId(newValue.id);
        setActivePackage(activePackageData);
        setErrors(prev => ({ ...prev, activePackage: '' }));
      } catch (error) {
        console.error('Error fetching active package:', error);
        setActivePackage(null);
      }
    } else {
      setAddress("");
      setUserId(null);
      setActivePackage(null);
      setSelectedUserTags([]);
    }
  }

  const handleRemoveItem = (index) => {
    const newItems = orderItems.filter((_, i) => i !== index);
    const updatedItems = newItems.map((item, i) => ({ ...item, id: i + 1 }));
    setOrderItems(updatedItems);
  };

  const handleQuantityChange = (index, value) => {
    const newItems = [...orderItems];
    newItems[index].quantity = value;
    setOrderItems(newItems);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    let validationErrors = {};
    
    if (shippingCost === undefined || shippingCost === '') validationErrors.shippingCost = 'Phí Ship không được để trống!';
    if (!address) validationErrors.address = 'Địa chỉ không được để trống!';

    // Validate order type consistency
    const orderType = data.get('orderType');
    const hasMonthlyPackageLineItem = orderItems.some(item => item.order_type === 'MONTHLY_PACKAGE_ORDER');
    const hasInstantOrderLineItem = orderItems.some(item => item.order_type === 'INSTANT_ORDER');

    if (orderType === 'MONTHLY_PACKAGE_ORDER') {
      if (!hasMonthlyPackageLineItem) {
        validationErrors.orderType = 'Đơn hàng gói tháng phải có ít nhất một sản phẩm thuộc gói tháng';
      }
      if (!activePackage) {
        validationErrors.activePackage = 'Khách hàng cần có gói tháng đang hoạt động để đặt đơn hàng gói tháng';
      }
    }

    if (orderType === 'INSTANT_ORDER' && hasMonthlyPackageLineItem) {
      validationErrors.orderType = 'Đơn hàng mua lẻ không thể có sản phẩm thuộc gói tháng';
    }

    // Check for duplicate products
    const productIds = orderItems.filter(item => item.product).map(item => item.product);
    const uniqueProductIds = [...new Set(productIds)];
    if (productIds.length !== uniqueProductIds.length) {
      const duplicateIds = productIds.filter((id, index) => productIds.indexOf(id) !== index);
      const duplicateNames = duplicateIds.map(id => {
        const product = products.find(p => p.id === id);
        return product?.name || id;
      });
      validationErrors.duplicateProducts = `Không được thêm sản phẩm trùng lặp: ${[...new Set(duplicateNames)].join(', ')}`;
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(Object.values(validationErrors).join('\n'), {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    else {
      const orderLineItems = orderItems.map(item => ({
        product_id: item.product,
        quantity: item.quantity,
        note: item.note || '',
        order_type: item.order_type || 'INSTANT_ORDER',
      }));
      const orderData = {
        user_id: userId,
        shipping_price: shippingCost,
        shipping_address: data.get('address'),
        order_line_items: orderLineItems,
        note: data.get('note'),
        order_type: data.get('orderType'),
        processed_at: data.get('processed_at') || null,
        tags: orderTags || null,
      };
      
      if (data.get('orderType') === 'MONTHLY_PACKAGE_ORDER' && activePackage) {
        orderData.user_package_id = activePackage.id;
      }
      
      const order = new Order(orderData);
      try {
        const res = await order.addOrder();
        if (res.success) {
          toast.success(res.messenge, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          navigate('/orders');
        }
        else if (res.requires_confirmation) {
          if (res.warning_type === 'duplicate_order') {
            // Show confirmation dialog for duplicate orders
            setExistingOrders(res.existing_orders || []);
            setPendingOrderData(orderData);
            setDuplicateOrderDialogOpen(true);
          } else {
            // Show confirmation dialog with inventory warnings
            setInventoryWarnings(res.inventory_warnings || []);
            setPendingOrderData(orderData);
            setConfirmDialogOpen(true);
          }
        }
        else {
          toast.error(res.messenge, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      }
      catch (error) {
        let errorMessage = "Đã xảy ra lỗi không xác định!";
        if (error.response?.data?.error) {
          if (Array.isArray(error.response.data.error)) {
            errorMessage = error.response.data.error.join(", ");
          } else if (typeof error.response.data.error === "string") {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data.error === "object") {
            errorMessage = JSON.stringify(error.response.data.error);
          }
        }
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  };

  const handleConfirmOrder = async () => {
    if (!pendingOrderData) return;

    const order = new Order(pendingOrderData);
    try {
      const res = await order.addOrder(true); // force_create = true
      if (res.success) {
        toast.success(res.messenge, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setConfirmDialogOpen(false);
        setPendingOrderData(null);
        setInventoryWarnings([]);
        navigate('/orders');
      } else {
        toast.error(res.messenge || "Đã xảy ra lỗi", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      let errorMessage = "Đã xảy ra lỗi không xác định!";
      if (error.response?.data?.error) {
        if (Array.isArray(error.response.data.error)) {
          errorMessage = error.response.data.error.join(", ");
        } else if (typeof error.response.data.error === "string") {
          errorMessage = error.response.data.error;
        }
      }
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
    setPendingOrderData(null);
    setInventoryWarnings([]);
  };

  const handleConfirmDuplicateOrder = async () => {
    if (!pendingOrderData) return;

    const order = new Order(pendingOrderData);
    try {
      const res = await order.addOrder(false, true); // skip_duplicate_check = true
      if (res.success) {
        toast.success(res.messenge, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setDuplicateOrderDialogOpen(false);
        setPendingOrderData(null);
        setExistingOrders([]);
        navigate('/orders');
      } else if (res.requires_confirmation && !res.warning_type) {
        // After bypassing duplicate check, inventory warnings may appear
        setDuplicateOrderDialogOpen(false);
        setExistingOrders([]);
        setInventoryWarnings(res.inventory_warnings || []);
        setConfirmDialogOpen(true);
      } else {
        toast.error(res.messenge || "Đã xảy ra lỗi", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      let errorMessage = "Đã xảy ra lỗi không xác định!";
      if (error.response?.data?.error) {
        if (Array.isArray(error.response.data.error)) {
          errorMessage = error.response.data.error.join(", ");
        } else if (typeof error.response.data.error === "string") {
          errorMessage = error.response.data.error;
        }
      }
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleCancelDuplicateOrder = () => {
    setDuplicateOrderDialogOpen(false);
    setPendingOrderData(null);
    setExistingOrders([]);
  };

  if (loading) {
    return <CircularProgress />;
  }
  return (
    <Container>
      <Box component="form" onSubmit={handleSubmit}>
        <Autocomplete
          options={customerOptions}
          getOptionLabel={(option) => option.name || ""}
          filterOptions={(x) => x}
          onChange={(event, newValue) => handleCustomerChange(newValue)}
          onInputChange={(event, newValue) => {
            setSearchTerm(newValue);
            if (newValue) {
              debouncedSearchCustomers(newValue);
            } else {
              setCustomerOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tìm Khách Hàng"
              margin="normal"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {customerLoading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              helperText="Nhập tên hoặc số điện thoại để tìm kiếm"
            />
          )}
          loading={customerLoading}
          fullWidth
          noOptionsText="Không tìm thấy khách hàng"
          loadingText="Đang tìm kiếm..."
        />
        <TextField
          id="address"
          name="address"
          label="Địa chỉ"
          fullWidth
          margin="normal"
          variant="outlined"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!errors.address}
          helperText={errors.address}
        />
        <Autocomplete
          value={shippingCost}
          onChange={(event, newValue) => {
            setShippingCost(newValue);
          }}
          options={[0, 10000, 15000, 20000, 25000, 29000, 39000]}
          getOptionLabel={(option) => {
            return option === 0 ? "Miễn phí" : new Intl.NumberFormat().format(option);
          }}
          onInputChange={(event, newInputValue) => {
            if (/^\d*$/.test(newInputValue)) {
              setShippingCost(newInputValue === '' ? '' : Number(newInputValue));
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tiền Ship"
              variant="outlined"
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.shippingCost}
              helperText={errors.shippingCost}
              inputProps={{
                ...params.inputProps,
                onKeyPress: (e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                {option === 0 ? "0" : new Intl.NumberFormat().format(option)}
              </li>
            );
          }}
          freeSolo
        />
        <TextField
          id="note"
          name="note"
          label="Ghi Chú"
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          rows={4}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          id="processed_at"
          name="processed_at"
          label="Ngày xử lý"
          type="date"
          defaultValue={new Date().toISOString().slice(0, 10)}
          fullWidth
          margin="normal"
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* User Tags Inheritance Section */}
        {userId && selectedUserTags.length > 0 && (
          <Box sx={{
            mt: 2,
            p: 2,
            bgcolor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ffcc80'
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tags của khách hàng (click để áp dụng):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {selectedUserTags.map((tag, index) => {
                const currentOrderTags = orderTags ? orderTags.split(',').map(t => t.trim()) : [];
                const isApplied = currentOrderTags.some(t => t.toLowerCase() === tag.toLowerCase());
                return (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    color={isApplied ? "default" : "warning"}
                    variant={isApplied ? "filled" : "outlined"}
                    onClick={() => {
                      if (!isApplied) {
                        const newTags = orderTags ? `${orderTags},${tag}` : tag;
                        if (newTags.length <= 200) {
                          setOrderTags(newTags);
                        }
                      }
                    }}
                    disabled={isApplied}
                    sx={{
                      cursor: isApplied ? 'default' : 'pointer',
                      opacity: isApplied ? 0.6 : 1
                    }}
                  />
                );
              })}
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => {
                const currentOrderTags = orderTags ? orderTags.split(',').map(t => t.trim()) : [];
                const newTags = [...currentOrderTags];
                selectedUserTags.forEach(tag => {
                  if (!newTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
                    newTags.push(tag);
                  }
                });
                const tagsString = newTags.join(',');
                if (tagsString.length <= 200) {
                  setOrderTags(tagsString);
                }
              }}
            >
              Áp dụng tất cả tags
            </Button>
          </Box>
        )}

        {/* Order Tags Input */}
        <Box sx={{ mt: 2 }}>
          <TagInput
            value={orderTags}
            onChange={setOrderTags}
            label="Tags đơn hàng"
            placeholder="Nhập tag và nhấn Enter (VD: Urgent, Priority)"
          />
        </Box>

        <TextField
          select
          id="orderType"
          name="orderType"
          label="Chọn Gói Sản Phẩm"
          fullWidth
          margin="normal"
          variant="outlined"
          value={currentOrderType}
          onChange={(e) => {
            const newOrderType = e.target.value;
            setCurrentOrderType(newOrderType);
            // Update all order items to use the new order type
            setOrderItems(prevItems => 
              prevItems.map(item => ({
                ...item,
                order_type: newOrderType
              }))
            );
          }}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!errors.orderType}
          helperText={errors.orderType}
        >
          <MenuItem value="MONTHLY_PACKAGE_ORDER">Gói Tháng</MenuItem>
          <MenuItem value="INSTANT_ORDER">Mua Lẻ</MenuItem>
        </TextField>
        
        {userId && (
          activePackage ? (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: '#e3f2fd',
              borderRadius: 1,
              border: '1px solid #90caf9'
            }}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              <strong>Thông tin gói tháng đang hoạt động:</strong>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Tên gói:</strong>{' '}
                  {activePackage.package ? (
                    <Button
                      component={Link}
                      to={`/user-packages/${activePackage.id}`}
                      sx={{
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline',
                          background: 'none'
                        }
                      }}
                    >
                      {activePackage.package.name}
                    </Button>
                  ) : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Ngày bắt đầu:</strong> {new Date(activePackage.beginning_date).toLocaleDateString('vi-VN')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Trạng thái:</strong> {activePackage.status || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Ngày kết thúc:</strong> {new Date(activePackage.ending_date).toLocaleDateString('vi-VN')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: activePackage.remaining_quantity > 0 ? '#e8f5e9' : '#ffebee',
                  borderRadius: 1,
                  border: activePackage.remaining_quantity > 0 ? '1px solid #a5d6a7' : '1px solid #ef9a9a'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: activePackage.remaining_quantity > 0 ? 'success.main' : 'error.main' }}>
                    Khối lượng còn lại tháng này: {(activePackage.remaining_quantity || 0).toFixed(2)} kg
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Định mức: {activePackage.package?.amount || 0} kg
                    {activePackage.bonus_total > 0 && ` + Tặng thêm: ${activePackage.bonus_total.toFixed(2)} kg`}
                    {' - '}Đã sử dụng: {(activePackage.used_monthly_quantity || 0).toFixed(2)} kg)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          ) : (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: '#ffebee', 
              borderRadius: 1,
              border: '1px solid #ffcdd2'
            }}>
              <Typography variant="subtitle1" color="error" gutterBottom>
                <strong>Thông tin gói tháng:</strong>
              </Typography>
              <Typography variant="body2" color="error">
                Khách hàng hiện không có gói tháng đang hoạt động
              </Typography>
            </Box>
          )
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '5%' }}>ID</TableCell>
                <TableCell sx={{ width: '35%' }}>Sản Phẩm</TableCell>
                <TableCell sx={{ width: '10%' }}>Số Lượng</TableCell>
                <TableCell sx={{ width: '15%' }}>Loại đơn</TableCell>
                <TableCell sx={{ width: '25%' }}>Ghi Chú</TableCell>
                <TableCell sx={{ width: '10%' }}>Xóa</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderItems.map((item, index) => (
                <React.Fragment key={index}>
                  <TableRow>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <Autocomplete
                        options={products}
                        getOptionLabel={(option) => option.name || ""}
                        value={products.find(p => p.id === item.product) || null}
                        onChange={(event, newValue) => handleProductChange(index, newValue ? newValue.id : '')}
                        renderInput={(params) => <TextField {...params} label="Sản Phẩm" fullWidth />}
                        renderOption={(props, option) => {
                          const { key, ...rest } = props;
                          return (
                            <li key={key} {...rest}>
                              {option.name}
                              {option.is_combo && (
                                <Chip label="Combo" size="small" color="secondary" sx={{ ml: 1 }} />
                              )}
                            </li>
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        value={item.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value)) {
                            handleQuantityChange(index, value);
                          }
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        value={item.order_type || 'INSTANT_ORDER'}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newItems = [...orderItems];
                          newItems[index].order_type = value;
                          setOrderItems(newItems);
                        }}
                        fullWidth
                      >
                        <MenuItem value="INSTANT_ORDER">Mua Lẻ</MenuItem>
                        <MenuItem value="MONTHLY_PACKAGE_ORDER">Gói Tháng</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        value={item.note || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newItems = [...orderItems];
                          newItems[index].note = value;
                          setOrderItems(newItems);
                        }}
                        fullWidth
                        label="Ghi Chú"
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="outlined" onClick={() => handleRemoveItem(index)}>X</Button>
                    </TableCell>
                  </TableRow>
                  {comboInfo[index] && comboInfo[index].isCombo && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Collapse in={true}>
                          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <InfoIcon color="secondary" sx={{ mr: 1 }} />
                              <Typography variant="subtitle2" color="secondary">
                                Thành phần Combo:
                              </Typography>
                            </Box>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Sản phẩm</TableCell>
                                  <TableCell align="right">Số lượng/combo</TableCell>
                                  <TableCell align="right">Tổng cần</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {comboInfo[index].components.map((comp, compIndex) => (
                                  <TableRow key={compIndex}>
                                    <TableCell>{comp.component_product?.name || 'N/A'}</TableCell>
                                    <TableCell align="right">{comp.quantity}</TableCell>
                                    <TableCell align="right">
                                      {(parseFloat(comp.quantity) * parseFloat(item.quantity || 0)).toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                              Kho sẽ trừ từng thành phần khi đặt hàng combo này
                            </Alert>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid container spacing={2} marginTop={2} direction="column" alignItems="center" justifyContent="center">
          <Grid item>
            <Button variant="contained" onClick={handleAddItem}>Thêm Sản Phẩm</Button>
          </Grid>
          <Grid item>
            <Button type="submit">Lưu Đơn Hàng</Button>
          </Grid>
        </Grid>
      </Box>

      {/* Inventory Warning Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Cảnh báo tồn kho không đủ
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Một số sản phẩm không đủ tồn kho. Bạn có chắc chắn muốn tạo đơn hàng?
          </Alert>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Sản phẩm</strong></TableCell>
                  <TableCell><strong>Từ Combo</strong></TableCell>
                  <TableCell align="right"><strong>Yêu cầu</strong></TableCell>
                  <TableCell align="right"><strong>Tồn kho</strong></TableCell>
                  <TableCell align="right"><strong>Thiếu</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(inventoryWarnings || []).map((warning, index) => (
                  <TableRow key={index}>
                    <TableCell>{warning.product_name}</TableCell>
                    <TableCell>
                      {warning.from_combo ? (
                        <Chip label={warning.from_combo} size="small" color="secondary" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{warning.requested_quantity}</TableCell>
                    <TableCell align="right">{warning.available_quantity}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                      -{warning.shortage}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelConfirm} variant="outlined">
            Hủy
          </Button>
          <Button onClick={handleConfirmOrder} variant="contained" color="warning">
            Vẫn tạo đơn hàng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Order Warning Dialog */}
      <Dialog
        open={duplicateOrderDialogOpen}
        onClose={handleCancelDuplicateOrder}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Cảnh báo: Khách hàng đã có đơn hàng trong ngày
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Khách hàng này đã có đơn hàng khác trong cùng ngày xử lý. Bạn có chắc chắn muốn tạo thêm đơn hàng?
          </Alert>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Đơn hàng hiện có:
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Mã đơn</strong></TableCell>
                  <TableCell><strong>Trạng thái</strong></TableCell>
                  <TableCell align="right"><strong>Tổng tiền</strong></TableCell>
                  <TableCell><strong>Ngày xử lý</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(existingOrders || []).map((order, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Link
                        to={`/orders/${order.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        {order.name}
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </Link>
                    </TableCell>
                    <TableCell>
                      {order.status === 'OPEN' ? (
                        <Chip label="Mới" size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} />
                      ) : order.status === 'PACKAGED' ? (
                        <Chip label="Đã đóng gói" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }} />
                      ) : order.status === 'CANCELLED' ? (
                        <Chip label="Đã hủy" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                      ) : order.status === 'SENT_INVOICE' ? (
                        <Chip label="Đã gửi hóa đơn" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                      ) : (
                        <Chip label={order.status} size="small" sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(order.total_price || 0)}
                    </TableCell>
                    <TableCell>
                      {order.processed_at ? new Date(order.processed_at).toLocaleDateString('vi-VN') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDuplicateOrder} variant="outlined">
            Hủy
          </Button>
          <Button onClick={handleConfirmDuplicateOrder} variant="contained" color="warning">
            Vẫn tạo đơn hàng
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddOrder;
